// BOORO: inbox contracts. The inbox collects what needs a person: approvals,
// questions, takeover requests, tasks to review and failures. Every item is
// answered or dismissed in place, so the list is always the open work.
import { oc } from "@orpc/contract";
import * as z from "zod";
import type { TicketStatus } from "./board.js";
import { Id } from "./ids.js";

/** What a person is asked for. */
export const INBOX_KINDS = [
  "approval",
  "question",
  "takeover",
  "review",
  "failure",
  "budget",
] as const;
export type InboxKind = (typeof INBOX_KINDS)[number];

/** An ask block as it is shown, so the card renders like the one in the chat. */
export const InboxAskBlockSchema = z.object({
  text: z.string(),
  detail: z.string().optional(),
  detailFormat: z.enum(["text", "code"]).optional(),
  /** Present when the ask needs an approval the person grants. */
  approvalEffectId: Id.optional(),
  /** "secret" asks are answered in the chat, not in the inbox. */
  input: z.enum(["text", "secret"]).optional(),
  purpose: z.enum(["password", "api_key", "code"]).optional(),
  status: z.enum(["pending", "answered"]).optional(),
  actions: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
        outcome: z.enum(["created", "cancelled"]).optional(),
      }),
    )
    .optional(),
});
export type InboxAskBlock = z.infer<typeof InboxAskBlockSchema>;

/** The task an inbox item belongs to, if it came from one. */
export const InboxTaskSchema = z.object({
  ticketId: Id,
  ref: z.string(),
  title: z.string(),
  status: z.string(),
  result: z.string().nullable(),
});
export type InboxTask = z.infer<typeof InboxTaskSchema>;

export const InboxItemSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("approval"),
    key: z.string(),
    /** The office the item belongs to; the list covers every office of the person. */
    spaceId: Id,
    botId: Id,
    botName: z.string(),
    botColor: z.string(),
    runId: Id,
    messageId: Id,
    block: InboxAskBlockSchema,
    task: InboxTaskSchema.nullable(),
    createdAt: z.string(),
  }),
  z.object({
    kind: z.literal("question"),
    key: z.string(),
    spaceId: Id,
    botId: Id,
    botName: z.string(),
    botColor: z.string(),
    runId: Id,
    messageId: Id,
    block: InboxAskBlockSchema,
    task: InboxTaskSchema.nullable(),
    createdAt: z.string(),
  }),
  z.object({
    kind: z.literal("takeover"),
    key: z.string(),
    spaceId: Id,
    botId: Id,
    botName: z.string(),
    botColor: z.string(),
    runId: Id,
    messageId: Id,
    task: InboxTaskSchema.nullable(),
    createdAt: z.string(),
  }),
  z.object({
    kind: z.literal("review"),
    key: z.string(),
    spaceId: Id,
    botId: Id.nullable(),
    botName: z.string().nullable(),
    botColor: z.string().nullable(),
    task: InboxTaskSchema,
    createdAt: z.string(),
  }),
  z.object({
    kind: z.literal("failure"),
    key: z.string(),
    spaceId: Id,
    botId: Id.nullable(),
    botName: z.string().nullable(),
    botColor: z.string().nullable(),
    runId: Id.nullable(),
    /** The error the run ended with, in one line. */
    error: z.string().nullable(),
    task: InboxTaskSchema.nullable(),
    createdAt: z.string(),
  }),
  z.object({
    kind: z.literal("budget"),
    key: z.string(),
    spaceId: Id,
    botId: Id.nullable(),
    botName: z.string().nullable(),
    botColor: z.string().nullable(),
    /** The budget that was reached. */
    budgetId: Id,
    /** How much was spent this month. */
    spentUsd: z.number().nullable(),
    /** How much the month may cost. */
    monthlyUsd: z.number(),
    /** Whether the budget is used up or only nearly. */
    level: z.enum(["warn", "limit"]),
    createdAt: z.string(),
  }),
]);
export type InboxItem = z.infer<typeof InboxItemSchema>;

export const InboxListSchema = z.object({
  items: z.array(InboxItemSchema),
  /** How many entries there are per kind, including the ones beyond the limit. */
  counts: z.record(z.enum(INBOX_KINDS), z.number()),
});
export type InboxList = z.infer<typeof InboxListSchema>;

export const InboxCountSchema = z.object({ count: z.number() });
export type InboxCount = z.infer<typeof InboxCountSchema>;

/** The states a task can be in when it waits for a person. */
export const REVIEW_TASK_STATUSES = ["review"] as const;
export type ReviewTaskStatus = (typeof REVIEW_TASK_STATUSES)[number];

/** A task waiting for a person to look at it. */
export function isReviewTaskStatus(status: TicketStatus): boolean {
  return (REVIEW_TASK_STATUSES as readonly string[]).includes(status);
}

export const booroInboxContract = {
  list: oc.output(InboxListSchema),
  count: oc.output(InboxCountSchema),
  dismiss: oc
    .input(
      z.object({
        itemKey: z.string().min(1).max(200),
        /** The office of the item; the current one when it is left out. */
        spaceId: Id.optional(),
      }),
    )
    .output(InboxCountSchema),
};
